import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Check, Clock, BookOpen, ArrowRight, Crown, TrendingUp, Package, Percent, Calendar, Play, ChevronLeft, ChevronRight, Gift, X, Star, Monitor, Users, MessageSquare, Award, Sparkles, ShoppingCart, Landmark, Download, ExternalLink, AlertTriangle, Upload, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ClassScheduleView from './ClassScheduleView';
import BonusCourseModal from './courses/BonusCourseModal';
import ConsultationModal from './courses/ConsultationModal';
import EnrollmentRequestModal from './courses/EnrollmentRequestModal';
import CurrencyToggle from './CurrencyToggle';
import ExpertBundleCard from './courses/ExpertBundleCard';
import GerenciamentoBIMCard from './courses/GerenciamentoBIMCard';
import SpecificBundleCard from './courses/SpecificBundleCard';
import ModuleCard from './courses/ModuleCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constantes de preços/checkout e o helper openHotmart foram movidos para ./courses/coursesConstants


const CoursesSection = ({ onLoginClick }) => {
  const { isAuthenticated, token } = useAuth();
  const { fmt, currency } = useCurrency();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bundles');
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [bonusCourse, setBonusCourse] = useState(null);
  const [consultationBundle, setConsultationBundle] = useState(null);
  const [enrollmentRequest, setEnrollmentRequest] = useState(null);
  const [expertTab, setExpertTab] = useState('ead_recorded');
  const [checkoutConfig, setCheckoutConfig] = useState({ modules: {}, bundles: {} });

  // Configuração de checkout Hotmart vinda do backend (fonte única de verdade).
  const MODULE_CHECKOUT = checkoutConfig.modules || {};
  const BUNDLE_CHECKOUT = checkoutConfig.bundles || {};

  const tabCards = [
    { value: 'bundles', label: 'Formações', desc: 'Trilhas completas do básico ao expert', icon: Crown, gradient: 'from-red-600 to-red-800', ring: 'ring-red-500' },
    { value: 'courses', label: 'Módulos Individuais', desc: 'Cursos avulsos em EaD Gravado', icon: BookOpen, gradient: 'from-orange-600 to-red-700', ring: 'ring-orange-500' },
    { value: 'professional', label: 'Cursos Profissionais', desc: 'Capacitação focada no mercado', icon: TrendingUp, gradient: 'from-blue-600 to-blue-800', ring: 'ring-blue-500' },
    { value: 'government', label: 'Órgãos Públicos', desc: 'Convênios e capacitação institucional', icon: Award, gradient: 'from-purple-600 to-purple-800', ring: 'ring-purple-500' },
  ];

  const revitExpertBundles = useMemo(() => bundles.filter(b => b.id === 6), [bundles]);
  const otherBundles = useMemo(() => bundles.filter(b => b.id !== 6), [bundles]);
  const paidCourses = useMemo(() => courses.filter(course => ![1, 2, 3].includes(course.id)), [courses]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, bundlesRes, schedulesRes, checkoutRes] = await Promise.all([
        axios.get(`${API}/courses`),
        axios.get(`${API}/bundles`),
        axios.get(`${API}/schedules`),
        axios.get(`${API}/checkout-config`)
      ]);
      setCourses(coursesRes.data);
      setBundles(bundlesRes.data);
      setCheckoutConfig(checkoutRes.data || { modules: {}, bundles: {} });
      
      // Encontrar o curso Design Review para o modal de bônus
      const designReview = coursesRes.data.find(c => c.id === 3);
      if (designReview) {
        setBonusCourse(designReview);
      }
      
      // Organizar schedules por bundle_id e modality
      const schedulesMap = {};
      schedulesRes.data.forEach(schedule => {
        const key = `${schedule.bundle_id}_${schedule.modality}`;
        if (!schedulesMap[key]) {
          schedulesMap[key] = [];
        }
        schedulesMap[key].push(schedule);
      });
      setSchedules(schedulesMap);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = (bundle, modality, modalityKey) => {
    if (!isAuthenticated) {
      toast.info('Crie sua conta ou faça login para se matricular');
      onLoginClick();
      return;
    }

    // Redirecionar para página de checkout
    navigate(`/checkout/${bundle.id}/${modalityKey}`);
  };

  const modalityLabels = {
    ead_recorded: 'EaD Gravado',
    ead_live: 'EaD Ao Vivo',
    presential: 'Presencial'
  };

  if (loading) {
    return (
      <section id="courses" className="py-24 bg-black">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center text-white">Carregando...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="courses" className="py-24 bg-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-0 w-96 h-96 bg-red-600 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <Badge className="bg-red-600/20 text-red-400 border-red-500/30 mb-4">Educação BIM</Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4">Formações e Cursos</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Escolha entre formações completas com economia de até 60% ou cursos individuais específicos.
          </p>
          <div className="mt-8">
            <CurrencyToggle />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Navegação em cards de destaque (carrossel) */}
          <div className="mb-12 flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory lg:grid lg:grid-cols-4 lg:overflow-visible" style={{ scrollbarWidth: 'none' }}>
            {tabCards.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  data-testid={`tab-card-${tab.value}`}
                  className={`relative shrink-0 w-[260px] lg:w-auto snap-start text-left rounded-2xl p-6 border transition-all duration-300 ${
                    isActive
                      ? `bg-gradient-to-br ${tab.gradient} border-transparent ring-2 ${tab.ring} ring-offset-2 ring-offset-black scale-[1.03] shadow-xl`
                      : 'bg-zinc-900 border-zinc-800 opacity-60 hover:opacity-100 hover:border-zinc-600 scale-100'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isActive ? 'bg-white/20' : 'bg-zinc-800'}`}>
                    <TabIcon size={26} className="text-white" />
                  </div>
                  <h3 className={`font-bold text-lg mb-1 ${isActive ? 'text-white' : 'text-gray-200'}`}>{tab.label}</h3>
                  <p className={`text-sm ${isActive ? 'text-white/85' : 'text-gray-500'}`}>{tab.desc}</p>
                  {isActive && (
                    <div className="mt-3 inline-flex items-center text-white text-xs font-semibold">
                      Selecionado <ArrowRight size={14} className="ml-1" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* FORMAÇÕES */}
          <TabsContent value="bundles" className="space-y-12">
            {/* Banner de Bônus - Clicável */}
            <div 
              onClick={() => setShowBonusModal(true)}
              className="bg-gradient-to-r from-green-900/30 to-emerald-900/20 border border-green-500/30 rounded-xl p-6 cursor-pointer hover:border-green-400 hover:shadow-lg hover:shadow-green-500/10 transition-all duration-300 group"
            >
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <Gift className="text-green-400 group-hover:scale-110 transition-transform" size={32} />
                <div className="text-center md:text-left flex-1">
                  <h3 className="text-xl font-bold text-green-400">🎁 Bônus Exclusivo em Todas as Formações!</h3>
                  <p className="text-gray-300">
                    Ganhe o curso <strong className="text-white">Autodesk | Design Review</strong> gratuitamente ao se matricular em qualquer formação.
                  </p>
                </div>
                <Button variant="outline" size="sm" className="border-green-500 text-green-400 hover:bg-green-600 hover:text-white">
                  Ver Detalhes
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </div>
            </div>

            {/* Destaque em Lançamento - Formação Gerenciamento BIM */}
            <GerenciamentoBIMCard />

            {/* Destaque Revit Expert - ID 6 é a formação completa */}
            {revitExpertBundles.map((bundle) => (
              <ExpertBundleCard
                key={bundle.id}
                bundle={bundle}
                fmt={fmt}
                currency={currency}
                expertTab={expertTab}
                setExpertTab={setExpertTab}
                schedules={schedules}
                bundleCheckout={BUNDLE_CHECKOUT}
                courses={courses}
                navigate={navigate}
                onEnroll={handleEnroll}
                onEnrollmentRequest={setEnrollmentRequest}
                onConsult={setConsultationBundle}
              />
            ))}

            {/* Outras Formações - Carrossel Horizontal */}
            <div>
              <h3 className="text-2xl font-bold text-white mb-6 text-center">Formações Específicas</h3>
              <div className="relative">
                {/* Botões de navegação */}
                <button
                  onClick={() => {
                    const container = document.getElementById('formations-carousel');
                    if (container) container.scrollBy({ left: -400, behavior: 'smooth' });
                  }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg -ml-4 hidden md:flex items-center justify-center"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={() => {
                    const container = document.getElementById('formations-carousel');
                    if (container) container.scrollBy({ left: 400, behavior: 'smooth' });
                  }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg -mr-4 hidden md:flex items-center justify-center"
                >
                  <ChevronRight size={24} />
                </button>

                {/* Carrossel */}
                <div 
                  id="formations-carousel"
                  className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {otherBundles.map((bundle) => (
                    <SpecificBundleCard
                      key={bundle.id}
                      bundle={bundle}
                      fmt={fmt}
                      currency={currency}
                      bundleCheckout={BUNDLE_CHECKOUT}
                      navigate={navigate}
                      onEnrollmentRequest={setEnrollmentRequest}
                      onConsult={setConsultationBundle}
                    />
                  ))}
                </div>

                {/* Indicador de scroll para mobile */}
                <div className="flex justify-center mt-4 md:hidden">
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <ChevronLeft size={16} />
                    <span>Deslize para ver mais formações</span>
                    <ChevronRight size={16} />
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* CURSOS INDIVIDUAIS */}
          <TabsContent value="courses">
            <div className="mb-8 bg-yellow-900/20 border border-yellow-700/30 rounded-xl p-6 text-center">
              <h3 className="text-xl font-bold text-yellow-400 mb-2">💡 Dica Importante</h3>
              <p className="text-gray-300">
                Comprar cursos individuais pode custar até <strong className="text-yellow-400">R$ 25.000+</strong>. 
                Com nossas formações, você economiza <strong className="text-green-400">até 60%</strong> e tem acesso a todo conteúdo!
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Filtra cursos gratuitos (IDs 1, 2, 3) - Design Review agora é bônus das formações */}
              {paidCourses.map((course) => (
                <ModuleCard
                  key={course.id}
                  course={course}
                  fmt={fmt}
                  currency={currency}
                  moduleCheckout={MODULE_CHECKOUT}
                  navigate={navigate}
                />
              ))}
            </div>
          </TabsContent>

          {/* CURSOS PROFISSIONAIS */}
          <TabsContent value="professional">
            <div className="text-center py-20">
              <div className="max-w-md mx-auto">
                <div className="w-20 h-20 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="text-blue-400" size={40} />
                </div>
                <h3 className="text-4xl font-bold text-white mb-4">Em breve!</h3>
                <p className="text-gray-400 text-lg">
                  Novos cursos profissionais estão sendo preparados.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* CURSOS PARA ÓRGÃOS PÚBLICOS */}
          <TabsContent value="government">
            <div className="space-y-8">
              
              {/* DESTAQUE - Curso BIM para Prefeituras */}
              <Card className="bg-gradient-to-br from-red-900/40 via-zinc-900 to-zinc-900 border-2 border-red-500 overflow-hidden relative">
                {/* Badge de Destaque */}
                <div className="absolute top-4 right-4 z-20">
                  <Badge className="bg-red-600 text-white text-sm px-4 py-2 animate-pulse">
                    <Calendar className="mr-2" size={16} />
                    INSCRIÇÕES ABERTAS
                  </Badge>
                </div>

                {/* Header com Imagem do Curso - Banner Expandido */}
                <div className="relative w-full">
                  <img 
                    src="/curso-bim-prefeituras-header.png" 
                    alt="BIM para Prefeituras"
                    className="w-full h-auto object-cover"
                  />
                </div>

                {/* Conteúdo Principal */}
                <div className="p-8 bg-zinc-900">
                  <div className="grid lg:grid-cols-2 gap-8">
                    {/* Coluna Esquerda */}
                    <div>
                      {/* Datas e Horário */}
                      <div className="bg-red-600/20 border border-red-500/30 rounded-xl p-6 mb-6">
                        <h3 className="text-xl font-bold text-red-400 mb-4 flex items-center">
                          <Calendar className="mr-2" size={20} />
                          Datas do Curso
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-center text-white">
                            <span className="w-32 text-gray-400">Dias:</span>
                            <span className="font-semibold">29 e 30 de Junho e 01 de Julho de 2026</span>
                          </div>
                          <div className="flex items-center text-white">
                            <span className="w-32 text-gray-400">Horário:</span>
                            <span className="font-semibold">08:00 às 17:00</span>
                          </div>
                          <div className="flex items-center text-white">
                            <span className="w-32 text-gray-400">Formato:</span>
                            <span className="font-semibold">Transmissão pela Internet</span>
                          </div>
                          <div className="flex items-center text-white">
                            <span className="w-32 text-gray-400">Carga Horária:</span>
                            <span className="font-semibold">24 horas</span>
                          </div>
                        </div>
                      </div>

                      {/* Para Quem é Este Curso */}
                      <div className="mb-6">
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <Users className="mr-2 text-red-400" size={20} />
                          Para Quem é Este Curso?
                        </h3>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="bg-zinc-800 rounded-lg p-4 text-center">
                            <Landmark className="text-red-400 mx-auto mb-2" size={28} />
                            <div className="text-white text-sm font-medium">Prefeitos(as)</div>
                          </div>
                          <div className="bg-zinc-800 rounded-lg p-4 text-center">
                            <Users className="text-red-400 mx-auto mb-2" size={28} />
                            <div className="text-white text-sm font-medium">Secretários(as) de Governo</div>
                          </div>
                          <div className="bg-zinc-800 rounded-lg p-4 text-center">
                            <Award className="text-red-400 mx-auto mb-2" size={28} />
                            <div className="text-white text-sm font-medium">Servidores(as) Públicos(as)</div>
                          </div>
                        </div>
                      </div>

                      {/* O Que Você Vai Aprender */}
                      <div>
                        <h3 className="text-xl font-bold text-white mb-4 flex items-center">
                          <BookOpen className="mr-2 text-red-400" size={20} />
                          O Que Você Vai Aprender?
                        </h3>
                        <div className="space-y-2">
                          {[
                            'Como usar o BIM em licitações públicas',
                            'Decretos BIM: como elaborar e implementar',
                            'Legislação BIM vigente',
                            'Normas Técnicas Brasileiras (ABNT) aplicáveis',
                            'Estudos de casos de prefeituras que já usam BIM',
                            'Boas práticas para uma implantação eficiente e sustentável'
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check className="text-red-400 mt-0.5 flex-shrink-0" size={16} />
                              <span className="text-gray-300 text-sm">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Coluna Direita */}
                    <div>
                      {/* Benefícios */}
                      <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-6 mb-6">
                        <h3 className="text-xl font-bold text-blue-400 mb-4 flex items-center">
                          <TrendingUp className="mr-2" size={20} />
                          Benefícios para Sua Gestão
                        </h3>
                        <div className="space-y-3">
                          {[
                            'Mais eficiência em obras e investimentos públicos',
                            'Processos licitatórios mais transparentes e seguros',
                            'Melhor planejamento e tomada de decisão',
                            'Conformidade com a legislação e normas técnicas'
                          ].map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check className="text-blue-400 mt-0.5 flex-shrink-0" size={16} />
                              <span className="text-gray-300 text-sm">{item}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Botão de Inscrição */}
                      <Button
                        onClick={() => window.open('https://doity.com.br/bim-para-prefeituras', '_blank')}
                        className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg mb-4"
                      >
                        Inscreva-se Já!
                        <ArrowRight className="ml-2" size={20} />
                      </Button>
                      <p className="text-gray-500 text-xs text-center">
                        Dê o próximo passo para modernizar sua prefeitura com o BIM
                      </p>
                    </div>
                  </div>

                  {/* Professores */}
                  <div className="border-t border-red-500/30 mt-8 pt-8">
                    <h3 className="text-2xl font-bold text-white mb-6 text-center">
                      Com Quem Você Vai Aprender?
                    </h3>
                    
                    {/* Imagem dos Professores */}
                    <div className="flex justify-center mb-6">
                      <img 
                        src="/professores-bim-prefeituras.png" 
                        alt="Professores do Curso BIM para Prefeituras"
                        className="max-w-full h-auto rounded-xl"
                      />
                    </div>
                    
                    {/* Descrições dos Professores */}
                    <div className="grid md:grid-cols-3 gap-6 mt-6">
                      <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                        <h4 className="text-lg font-bold text-white mb-1">Alexander Justi</h4>
                        <p className="text-red-400 text-sm font-medium mb-2">Msc. Arquiteto | Especialista Estratégico</p>
                        <p className="text-gray-400 text-xs">
                          Fundador do AJ BIM Portal, pioneiro na formação de profissionais em Revit no Brasil desde 2005.
                        </p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                        <h4 className="text-lg font-bold text-white mb-1">Alessandro Lopes</h4>
                        <p className="text-red-400 text-sm font-medium mb-2">Arquiteto | Especialista em BIM</p>
                        <p className="text-gray-400 text-xs">
                          Especialista em BIM com ampla experiência em projetos de grande porte e implantação em organizações públicas.
                        </p>
                      </div>
                      <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                        <h4 className="text-lg font-bold text-white mb-1">Nicola Khoury</h4>
                        <p className="text-red-400 text-sm font-medium mb-2">Engenheiro | Secretário do TCU</p>
                        <p className="text-gray-400 text-xs">
                          Engenheiro e Secretário do Tribunal de Contas da União. Especialista em fiscalização de obras públicas.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Curso BIM para Órgãos Públicos */}
              <Card className="bg-gradient-to-br from-purple-900/30 via-zinc-900 to-zinc-900 border-2 border-purple-500 overflow-hidden">
                <div className="grid lg:grid-cols-2 gap-0">
                  {/* Lado Esquerdo - Imagem e Info */}
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-indigo-800">
                      <img 
                        src="https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=800&q=80" 
                        alt="BIM para Órgãos Públicos"
                        className="w-full h-full object-cover opacity-30"
                      />
                    </div>
                    <div className="relative p-8 flex flex-col justify-center min-h-[400px]">
                      <Badge className="bg-purple-500 text-white mb-4 w-fit">
                        Capacitação Especializada
                      </Badge>
                      <h2 className="text-4xl font-bold text-white mb-4">
                        BIM para Órgãos Públicos
                      </h2>
                      <p className="text-purple-100 text-lg mb-6">
                        Diretrizes para Contratações Públicas com uso de BIM alinhadas à Lei 14.133/2021
                      </p>
                      <div className="flex flex-wrap gap-4">
                        <div className="bg-black/30 backdrop-blur rounded-lg px-4 py-2">
                          <div className="text-2xl font-bold text-white">24h</div>
                          <div className="text-xs text-purple-200">Carga Horária</div>
                        </div>
                        <div className="bg-black/30 backdrop-blur rounded-lg px-4 py-2">
                          <div className="text-2xl font-bold text-white">100%</div>
                          <div className="text-xs text-purple-200">Online Ao Vivo</div>
                        </div>
                        <div className="bg-black/30 backdrop-blur rounded-lg px-4 py-2">
                          <div className="text-2xl font-bold text-white">Certificado</div>
                          <div className="text-xs text-purple-200">Incluso</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lado Direito - Conteúdo */}
                  <div className="p-8 bg-zinc-900">
                    {/* Apresentação */}
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-white mb-3 flex items-center">
                        <BookOpen className="mr-2 text-purple-400" size={20} />
                        Apresentação
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        O processo de contratação pública no Brasil está em transformação, incorporando práticas mais eficientes, 
                        digitais e integradas. Este curso oferece uma capacitação completa e prática sobre como estruturar 
                        contratações utilizando a Modelagem da Informação da Construção (BIM), em conformidade com a Lei 14.133/2021.
                      </p>
                      <p className="text-gray-300 text-sm leading-relaxed mt-3">
                        Em 24 horas de capacitação intensiva e ao vivo, os participantes explorarão desde os fundamentos do BIM 
                        até sua aplicação prática em editais, contratos, fiscalização e uso do CDE (Ambiente Comum de Dados).
                      </p>
                    </div>

                    {/* Objetivos */}
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-white mb-3 flex items-center">
                        <Star className="mr-2 text-purple-400" size={20} />
                        Objetivos
                      </h3>
                      <ul className="space-y-2">
                        {[
                          'Capacitar participantes para elaborar e analisar processos de contratação pública com BIM',
                          'Garantir conformidade com a Lei 14.133/2021 e segurança jurídica',
                          'Demonstrar uso prático de ferramentas como ChatGPT e Autodesk ACC',
                          'Otimizar criação de Termos de Referência, Editais e Contratos',
                          'Aprimorar acompanhamento e fiscalização de obras com modelos BIM'
                        ].map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                            <Check size={14} className="text-purple-400 mt-1 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Preço e CTA */}
                    <div className="bg-purple-900/30 border border-purple-500/30 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <div className="text-gray-400 text-sm">Investimento</div>
                          <div className="text-3xl font-bold text-white">R$ 3.200,00</div>
                          <div className="text-purple-300 text-sm">por pessoa</div>
                        </div>
                        <div className="text-right">
                          <div className="text-gray-400 text-sm">Formato</div>
                          <div className="text-white font-semibold">Online Ao Vivo</div>
                          <div className="text-purple-300 text-sm">via Internet</div>
                        </div>
                      </div>
                      <Button
                        onClick={() => window.open('https://wa.me/5561995792667?text=Olá! Tenho interesse no curso BIM para Órgãos Públicos.', '_blank')}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-6 text-lg"
                      >
                        Entre em contato agora e se inscreva
                        <ArrowRight className="ml-2" size={20} />
                      </Button>
                      <p className="text-purple-300 text-sm text-center mt-3 font-medium">
                        Condições especiais para grupos acima de 10 alunos
                      </p>
                      <p className="text-gray-500 text-xs text-center mt-2">
                        * Emissão de Nota de Empenho disponível para órgãos públicos
                      </p>
                    </div>
                  </div>
                </div>

                {/* Programa Didático Expandido */}
                <div className="border-t border-purple-500/30 bg-zinc-900/50 p-8">
                  <h3 className="text-2xl font-bold text-white mb-6 text-center flex items-center justify-center">
                    <Calendar className="mr-3 text-purple-400" size={24} />
                    Programa Didático - 24 horas
                  </h3>
                  
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      {
                        module: 'Módulo 1 - Fundamentos do BIM (4h)',
                        topics: ['Conceitos e princípios do BIM', 'Níveis de maturidade BIM', 'BIM no contexto brasileiro', 'Estratégia BIM BR e Decreto 10.306']
                      },
                      {
                        module: 'Módulo 2 - Marco Legal (4h)',
                        topics: ['Lei 14.133/2021 e BIM', 'Requisitos legais para contratações', 'Documentação técnica exigida', 'Segurança jurídica em contratos BIM']
                      },
                      {
                        module: 'Módulo 3 - Editais e Contratos (4h)',
                        topics: ['Estruturação de editais com BIM', 'Cláusulas contratuais específicas', 'Critérios de qualificação técnica', 'Modelos de Termo de Referência']
                      },
                      {
                        module: 'Módulo 4 - CDE e Ambiente Colaborativo (4h)',
                        topics: ['Ambiente Comum de Dados (CDE)', 'Autodesk Construction Cloud (ACC)', 'Fluxos de trabalho colaborativo', 'Gestão de modelos e documentos']
                      },
                      {
                        module: 'Módulo 5 - Fiscalização com BIM (4h)',
                        topics: ['Acompanhamento de obras com BIM', 'Verificação de conformidade', 'Relatórios e indicadores', 'Resolução de conflitos']
                      },
                      {
                        module: 'Módulo 6 - Ferramentas e IA (4h)',
                        topics: ['ChatGPT para documentos técnicos', 'Automação de análises', 'Verificação de modelos', 'Casos práticos e exercícios']
                      }
                    ].map((mod, idx) => (
                      <div key={idx} className="bg-zinc-800 rounded-xl p-5 border border-zinc-700 hover:border-purple-500/50 transition-colors">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {idx + 1}
                          </span>
                          <h4 className="text-white font-semibold text-sm">{mod.module}</h4>
                        </div>
                        <ul className="space-y-1.5">
                          {mod.topics.map((topic, tidx) => (
                            <li key={tidx} className="flex items-start gap-2 text-xs text-gray-400">
                              <Check size={10} className="text-purple-400 mt-1 flex-shrink-0" />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Professor */}
                <div className="border-t border-purple-500/30 bg-gradient-to-r from-zinc-900 to-purple-900/20 p-8">
                  <h3 className="text-2xl font-bold text-white mb-6 text-center">Professor</h3>
                  <div className="flex flex-col md:flex-row items-center gap-8 max-w-4xl mx-auto">
                    <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-purple-500 flex-shrink-0">
                      <img 
                        src="/professor-alexander-justi.jpg" 
                        alt="Professor Alexander Justi"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-center md:text-left">
                      <h4 className="text-2xl font-bold text-white mb-2">Alexander Justi</h4>
                      <p className="text-purple-400 font-semibold mb-4">Especialista BIM | Fundador do AJ BIM Portal</p>
                      <div className="text-gray-300 text-sm space-y-2">
                        <p>
                          Pioneiro na formação de profissionais em Revit no Brasil desde 2005. 
                          Fundador e CEO do AJ | BIM Portal, a maior plataforma de educação BIM da América Latina.
                        </p>
                        <p>
                          Autor do livro "Revit Architecture" e apresentador do BIMTALKS, com mais de 200 entrevistas 
                          com profissionais de referência no mercado BIM.
                        </p>
                        <p>
                          Consultor em implantação BIM para órgãos públicos e empresas privadas, 
                          com mais de 12.000 alunos formados em sua metodologia.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4 justify-center md:justify-start">
                        <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/30">+12.000 Alunos</Badge>
                        <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/30">Desde 2005</Badge>
                        <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/30">Autor de Livro</Badge>
                        <Badge className="bg-purple-600/20 text-purple-300 border-purple-500/30">BIMTALKS</Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Info Box */}
              <div className="bg-purple-900/20 border border-purple-700/30 rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold text-purple-400 mb-2">Capacitação para Equipes</h3>
                <p className="text-gray-300 mb-4">
                  Oferecemos condições especiais para turmas fechadas de órgãos públicos. 
                  Entre em contato para personalização do programa.
                </p>
                <Button
                  onClick={() => window.open('https://wa.me/5561995792667', '_blank')}
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-600 hover:text-white"
                >
                  Solicitar Proposta para Turma Fechada
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Seção de Download de Softwares */}
      <div className="container mx-auto px-4 mt-20">
        <Card className="bg-gradient-to-br from-blue-900/30 via-zinc-900 to-zinc-900 border border-blue-500/30 overflow-hidden">
          <div className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Ícone e Título */}
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-blue-600/20 rounded-2xl flex items-center justify-center">
                  <Download className="text-blue-400" size={48} />
                </div>
              </div>
              
              {/* Conteúdo */}
              <div className="flex-1 text-center md:text-left">
                <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30 mb-4">
                  Acesso Gratuito para Estudantes
                </Badge>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Softwares Autodesk para <span className="text-blue-400">Download</span>
                </h3>
                <p className="text-gray-400 mb-6 max-w-2xl">
                  Alunos e professores podem se registrar na área educacional da Autodesk e baixar 
                  gratuitamente os softwares como <strong className="text-white">Revit</strong>, <strong className="text-white">AutoCAD</strong>, <strong className="text-white">Navisworks</strong> e 
                  outros para fins estudantis. Aproveite essa oportunidade para praticar com as 
                  mesmas ferramentas utilizadas no mercado profissional.
                </p>
                
                {/* Benefícios */}
                <div className="flex flex-wrap gap-4 mb-6 justify-center md:justify-start">
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="text-green-400" size={16} />
                    <span>100% Gratuito</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="text-green-400" size={16} />
                    <span>Licença Educacional</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="text-green-400" size={16} />
                    <span>Versão Completa</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-300">
                    <Check className="text-green-400" size={16} />
                    <span>Atualizações Incluídas</span>
                  </div>
                </div>
                
                {/* Botão */}
                <Button
                  onClick={() => window.open('https://www.autodesk.com/br/education/edu-software/overview', '_blank')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
                >
                  <Download className="mr-2" size={20} />
                  Acessar Área de Download Autodesk
                  <ExternalLink className="ml-2" size={16} />
                </Button>
                
                <p className="text-gray-500 text-xs mt-4">
                  * Necessário cadastro com e-mail institucional ou comprovante de matrícula
                </p>
              </div>
              
              {/* Logo Autodesk */}
              <div className="flex-shrink-0 hidden lg:block">
                <div className="w-40 h-32 bg-white rounded-xl flex items-center justify-center p-4">
                  <span className="text-2xl font-bold text-black tracking-tight">AUTODESK</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Modal do Curso Bônus */}
      <BonusCourseModal 
        isOpen={showBonusModal}
        onClose={() => setShowBonusModal(false)}
        course={bonusCourse}
        onEnrollSeparately={(courseId) => {
          setShowBonusModal(false);
          const course = courses.find(c => c.id === courseId);
          if (course) {
            handleEnroll(course, 'EaD Gravado', 'ead_recorded');
          }
        }}
      />

      <ConsultationModal
        bundle={consultationBundle}
        onClose={() => setConsultationBundle(null)}
      />
      <EnrollmentRequestModal
        open={!!enrollmentRequest}
        bundle={enrollmentRequest?.bundle}
        conditionLabel={enrollmentRequest?.label}
        onClose={() => setEnrollmentRequest(null)}
      />
    </section>
  );
};

export default CoursesSection;